import React, { useState, useContext, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import dayjs from 'dayjs';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import DoiRateOverTimeChart from '../../components/DoiRateOverTimeChart';

// Interfaces for API data
interface DoiRateByOriginData {
  origin_host: string;
  doi_rate: number | null;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
}

interface DoiRateByEmailDomainData {
  email_domain: string;
  doi_rate: number | null;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
  email_percentage: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`doi-tabpanel-${index}`}
      aria-labelledby={`doi-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `doi-tab-${index}`,
    'aria-controls': `doi-tabpanel-${index}`,
  };
}

const DoiEvaluationPage = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tabValue, setTabValue] = useState(0);
  const [ratesByOrigin, setRatesByOrigin] = useState<DoiRateByOriginData[]>([]);
  const [availableOriginHosts, setAvailableOriginHosts] = useState<string[]>([]);
  const [selectedOriginHostForFilter, setSelectedOriginHostForFilter] = useState<string>('all');
  const [selectedOriginHostForTable, setSelectedOriginHostForTable] = useState<string | null>(null);
  const [selectedEmailDomain, setSelectedEmailDomain] = useState<string | null>(null);

  const originHeaderMapping: Record<string, HeaderMappingItem> = {
    origin_host: { name: 'Origin Host', type: 'string' },
    doi_rate: { name: 'DOI Rate', type: 'percentage', digits: 2 },
    total_distinct_emails: { name: 'Total Emails', type: 'number' },
    confirmed_distinct_emails: { name: 'Confirmed Emails', type: 'number' },
  };

  const emailDomainHeaderMapping: Record<string, HeaderMappingItem> = {
    email_domain: { name: 'Email Domain', type: 'string' },
    doi_rate: { name: 'DOI Rate', type: 'percentage', digits: 2 },
    email_percentage: { name: '% of All Emails', type: 'percentage', digits: 2 },
    total_distinct_emails: { name: 'Total Emails', type: 'number' },
    confirmed_distinct_emails: { name: 'Confirmed Emails', type: 'number' },
  };

  // Compute summary for origin rates
  const computeOriginSummary = (data: DoiRateByOriginData[]): DoiRateByOriginData => {
    if (!data.length) {
      return {
        origin_host: 'Insgesamt',
        doi_rate: 0,
        total_distinct_emails: 0,
        confirmed_distinct_emails: 0,
      };
    }

    const summary: DoiRateByOriginData = {
      origin_host: 'Insgesamt',
      doi_rate: 0,
      total_distinct_emails: 0,
      confirmed_distinct_emails: 0,
    };

    // Sum up the totals
    data.forEach((item) => {
      summary.total_distinct_emails += item.total_distinct_emails || 0;
      summary.confirmed_distinct_emails += item.confirmed_distinct_emails || 0;
    });

    // Calculate the overall DOI rate from the totals
    summary.doi_rate =
      summary.total_distinct_emails > 0
        ? summary.confirmed_distinct_emails / summary.total_distinct_emails
        : 0;

    return summary;
  };

  // Compute summary for email domain rates
  const computeEmailDomainSummary = (
    data: DoiRateByEmailDomainData[],
  ): DoiRateByEmailDomainData => {
    if (!data.length) {
      return {
        email_domain: 'Insgesamt',
        doi_rate: 0,
        total_distinct_emails: 0,
        confirmed_distinct_emails: 0,
        email_percentage: 1, // 100% of total
      };
    }

    const summary: DoiRateByEmailDomainData = {
      email_domain: 'Insgesamt',
      doi_rate: 0,
      total_distinct_emails: 0,
      confirmed_distinct_emails: 0,
      email_percentage: 1, // Will always be 100% of total
    };

    // Sum the totals
    data.forEach((item) => {
      summary.total_distinct_emails += item.total_distinct_emails;
      summary.confirmed_distinct_emails += item.confirmed_distinct_emails;
    });

    // Calculate the overall DOI rate
    summary.doi_rate =
      summary.total_distinct_emails > 0
        ? summary.confirmed_distinct_emails / summary.total_distinct_emails
        : 0;

    return summary;
  };

  // Fetch DOI rates by origin_host
  const fetchOriginRates = useCallback(
    async (currentFilterState: IFilterState): Promise<DoiRateByOriginData[]> => {
      if (!currentFilterState.startDate) return [];
      try {
        const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
        const params: any = { startDate: formattedStartDate };

        if (currentFilterState.endDate) {
          params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
        }

        const response = await fetchData(`/api/doi-evaluation/rates-by-origin`, params);

        // Handle response safely
        let resultArray: DoiRateByOriginData[] = [];

        if (Array.isArray(response)) {
          // Handle both flat arrays and nested arrays
          if (response.length > 0 && Array.isArray(response[0])) {
            // Handle nested arrays by flattening
            const flattenedArray = (response as any[]).flat();
            resultArray = flattenedArray.filter(
              (item) => item && typeof item === 'object' && 'origin_host' in item,
            ) as DoiRateByOriginData[];
          } else {
            // Handle normal arrays with type checking
            resultArray = response.filter(
              (item) => item && typeof item === 'object' && 'origin_host' in item,
            ) as DoiRateByOriginData[];
          }
        }

        setRatesByOrigin(resultArray);

        // Populate available origin hosts for the filter dropdown
        const hosts = Array.from(new Set(resultArray.map((item) => item.origin_host)));
        setAvailableOriginHosts(['all', ...hosts]);

        return resultArray;
      } catch (error) {
        console.error('Error fetching DOI rates by origin:', error);
        setRatesByOrigin([]);
        setAvailableOriginHosts(['all']);
        return [];
      }
    },
    [],
  );

  // Fetch DOI rates by email_domain
  const fetchEmailDomainRates = useCallback(
    async (currentFilterState: IFilterState): Promise<DoiRateByEmailDomainData[]> => {
      if (!selectedOriginHostForTable || !currentFilterState.startDate) {
        return [];
      }

      try {
        const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
        const params: any = {
          originHost: selectedOriginHostForTable,
          startDate: formattedStartDate,
        };

        if (currentFilterState.endDate) {
          params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
        }

        const response = await fetchData(`/api/doi-evaluation/rates-by-email-domain`, params);

        // Handle response safely
        let resultArray: DoiRateByEmailDomainData[] = [];

        if (Array.isArray(response)) {
          // Handle both flat arrays and nested arrays
          if (response.length > 0 && Array.isArray(response[0])) {
            // Handle nested arrays by flattening
            const flattenedArray = (response as any[]).flat();
            resultArray = flattenedArray.filter(
              (item) => item && typeof item === 'object' && 'email_domain' in item,
            ) as DoiRateByEmailDomainData[];
          } else {
            // Handle normal arrays with type checking
            resultArray = response.filter(
              (item) => item && typeof item === 'object' && 'email_domain' in item,
            ) as DoiRateByEmailDomainData[];
          }
        }

        return resultArray;
      } catch (error) {
        console.error('Error fetching DOI rates by email domain:', error);
        return [];
      }
    },
    [selectedOriginHostForTable],
  );

  const handleOriginHostFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedOriginHostForFilter(value);

    // If "all" is not selected, also set it for the table to directly load it
    if (value !== 'all') {
      setSelectedOriginHostForTable(value);
    } else {
      setSelectedOriginHostForTable(null); // Clear second table if "all" is selected
    }
  };

  const handleOriginRowClick = (rowData: DoiRateByOriginData) => {
    if (rowData && rowData.origin_host) {
      setSelectedOriginHostForTable(rowData.origin_host);
      setSelectedOriginHostForFilter(rowData.origin_host); // Sync dropdown
    }
  };

  const handleEmailDomainRowClick = (rowData: DoiRateByEmailDomainData) => {
    if (rowData && rowData.email_domain) {
      setSelectedEmailDomain(rowData.email_domain);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load data on component mount and when filter changes
  useEffect(() => {
    fetchOriginRates(filterState);
  }, [filterState.startDate, filterState.endDate, fetchOriginRates]);

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb="20px">
        Auswertung DOI-Raten
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="origin-host-filter-label">
                Origin Host (für Email Domain Tabelle)
              </InputLabel>
              <Select
                labelId="origin-host-filter-label"
                value={selectedOriginHostForFilter}
                label="Origin Host (für Email Domain Tabelle)"
                onChange={handleOriginHostFilterChange}
              >
                {availableOriginHosts.map((host) => (
                  <MenuItem key={host} value={host}>
                    {host === 'all' ? 'Alle Hosts (Tabelle unten manuell auswählen)' : host}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="DOI rate tabs">
          <Tab label="Tabellen" {...a11yProps(0)} />
          <Tab label="Zeitverlauf" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
              <Typography variant="h6" mb={2} fontWeight="bold">
                DOI-Raten nach Host
              </Typography>
              <TableWidget<DoiRateByOriginData>
                getData={() => fetchOriginRates(filterState)}
                computeSummary={computeOriginSummary}
                detailsHeaderMapping={originHeaderMapping}
                initialSortColumn="origin_host"
                excelDownloadName="doi_rates_by_origin"
                onRowClick={handleOriginRowClick}
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            {selectedOriginHostForTable ? (
              <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
                <Typography variant="h6" mb={2} fontWeight="bold">
                  DOI-Raten nach Email-Domain für {selectedOriginHostForTable}
                </Typography>
                <TableWidget<DoiRateByEmailDomainData>
                  key={`email-domain-table-${selectedOriginHostForTable}`}
                  getData={() => fetchEmailDomainRates(filterState)}
                  computeSummary={computeEmailDomainSummary}
                  detailsHeaderMapping={emailDomainHeaderMapping}
                  initialSortColumn="total_distinct_emails"
                  sortOrder="desc"
                  excelDownloadName={`doi_rates_email_domain_${selectedOriginHostForTable}`}
                  onRowClick={handleEmailDomainRowClick}
                />
              </Paper>
            ) : (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '10px',
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography>
                  Bitte einen Origin Host aus der oberen Tabelle auswählen oder oben im Filter
                  wählen, um die Email Domain Raten anzuzeigen.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
              <Typography variant="h6" mb={2} fontWeight="bold">
                DOI-Rate im Zeitverlauf
                {selectedOriginHostForTable && ` - ${selectedOriginHostForTable}`}
                {selectedEmailDomain && ` - ${selectedEmailDomain}`}
              </Typography>
              {selectedOriginHostForTable ? (
                selectedEmailDomain ? (
                  <DoiRateOverTimeChart
                    originHost={selectedOriginHostForTable}
                    emailDomain={selectedEmailDomain}
                  />
                ) : (
                  <DoiRateOverTimeChart originHost={selectedOriginHostForTable} />
                )
              ) : (
                <Box p={3} textAlign="center">
                  <Typography>
                    Bitte wählen Sie einen Origin Host aus der Tabelle oder Filter, um den DOI-Rate
                    Zeitverlauf anzuzeigen.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          {selectedOriginHostForTable && selectedEmailDomain && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Email Domain: {selectedEmailDomain}
                  </Typography>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      color: colors.grey[100],
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    onClick={() => setSelectedEmailDomain(null)}
                  >
                    <Typography variant="body2">Zurücksetzen</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default DoiEvaluationPage;
